import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import orderBy from "lodash.orderby"
import { WingsPagination, WingsStoryCard } from "@wingspans-ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { faSearch } from "@fortawesome/pro-regular-svg-icons"
import { useQueryParam, JsonParam, StringParam } from "use-query-params"
import { FormattedMessage, useIntl } from "react-intl"
import {
	localizeEducationCheckboxTranslations,
	localizeIdentityCheckboxTranslation,
	localizeInterestCheckboxTranslation,
	localizePersonalityCheckboxTranslation,
} from "../algolia/localized-algolia-checkboxes"
import useWeightedFuseSearch from "../hooks/useWeightedFuseSearch"
import { useAllStories } from "../../data/graphql"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"

import "../algolia/algolia.css"
import "../algolia/algolia-explore.css"

const STORIES_PER_PAGE = 6
const FILTER_ID_SEPARATOR = "::"

const initialRefinementList = {
	story_personality_filter: [],
	story_interests_filter: [],
	story_identity_filter: [],
	story_required_education_filter: [],
}

const storiesSearchKeys = [
	{ name: "story_job_title", weight: 0.05 },
	{ name: "title", weight: 0.22 },
	{ name: "story_keywords", weight: 0.17 },
	{ name: "story_career_synonyms_text", weight: .5 },
	{ name: "story_school_reference_titles_text", weight: 0.5 },
	{ name: "story_company", weight: 0.08 },
	{ name: "story_location", weight: 0.04 },
]

const storiesSearchOptions = {
	threshold: 0.3,
	ignoreLocation: true,
}

const normalizeText = (value) => {
	if (Array.isArray(value)) {
		return value.map((item) => normalizeText(item)).join(" ")
	}

	if (value && typeof value === "object") {
		return normalizeText(Object.values(value))
	}

	if (value === null || value === undefined) {
		return ""
	}

	return String(value).toLowerCase().trim()
}

let lastParsedQuery = ""
let lastParsedQueryTokens = []

const getQueryTokens = (query) => {
	const normalizedQuery = normalizeText(query)

	if (normalizedQuery === lastParsedQuery) {
		return { normalizedQuery, tokens: lastParsedQueryTokens }
	}

	const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
	lastParsedQuery = normalizedQuery
	lastParsedQueryTokens = tokens

	return { normalizedQuery, tokens }
}

const getStoryIdentity = (story) => {
	return story?.objectID || story?.id || story?.slug
}

const getStorySchoolReferenceTitles = (story) => {
	const schoolReferences = story?.story_school_reference
	if (!Array.isArray(schoolReferences)) {
		return []
	}

	return schoolReferences
		.map((schoolReference) => schoolReference?.title)
		.filter(Boolean)
}

// Two words are treated as the same "root" if one contains the other, or if they
// share a long-enough starting prefix (e.g. "brazier" vs "brazing" both start with
// "brazi"). This lets gerund/word-form variants match without full stemming rules.
const WORD_ROOT_MIN_LENGTH = 4
const WORD_ROOT_PREFIX_LENGTH = 5

const sharesWordRoot = (tokenWord, targetWord) => {
	if (!tokenWord || !targetWord) {
		return false
	}

	if (targetWord.includes(tokenWord) || tokenWord.includes(targetWord)) {
		return true
	}

	const minLength = Math.min(tokenWord.length, targetWord.length)
	if (minLength < WORD_ROOT_MIN_LENGTH) {
		return false
	}

	const prefixLength = Math.min(WORD_ROOT_PREFIX_LENGTH, minLength)
	return tokenWord.slice(0, prefixLength) === targetWord.slice(0, prefixLength)
}

const tokenMatchesText = (token, text) => {
	if (!token) {
		return true
	}

	if (!text) {
		return false
	}

	if (text.includes(token)) {
		return true
	}

	const words = text.split(/[^a-z0-9]+/).filter(Boolean)
	return words.some((word) => sharesWordRoot(token, word))
}

const getStoryCareerSynonymPriority = (story, query) => {
	const { normalizedQuery, tokens } = getQueryTokens(query)

	if (!normalizedQuery || !tokens.length) {
		return 0
	}

	const synonyms = story?.story_career_synonyms || []
	if (!synonyms.length) {
		return 0
	}

	const normalizedSynonyms = synonyms.map((synonym) => normalizeText(synonym)).filter(Boolean)
	if (!normalizedSynonyms.length) {
		return 0
	}

	// Highest priority: the query is an exact match against one of the individual,
	// comma-separated career synonym values (not just a substring hit anywhere in a
	// combined blob). This surfaces a precise synonym match (e.g. "Program Administrator")
	// above both a partial/substring synonym match and any title match.
	const hasExactMatch = normalizedSynonyms.some((synonym) => synonym === normalizedQuery)
	if (hasExactMatch) {
		return 2
	}

	// Second priority: intentionally strict (exact substring only, no word-root fuzzy
	// matching) because this hard-boosts a story near the top of the results - a loose
	// match here would surface irrelevant stories prominently instead of just including
	// them further down. Checked per individual synonym so a match can't accidentally
	// span across two different concatenated synonym values.
	const hasPartialMatch = normalizedSynonyms.some((synonym) => {
		return synonym.includes(normalizedQuery) || tokens.some((token) => synonym.includes(token))
	})

	return hasPartialMatch ? 1 : 0
}

const storyMatchesSearchTokens = (story, query) => {
	const { normalizedQuery, tokens } = getQueryTokens(query)
	if (!normalizedQuery) {
		return true
	}

	if (!tokens.length) {
		return true
	}

	const searchableText = normalizeText([
		story?.story_searchable_text,
		story?.story_job_title,
		story?.title,
		story?.story_keywords,
		story?.story_company,
		story?.story_location,
		story?.story_career_synonyms_text,
		getStorySchoolReferenceTitles(story),
	])

	const matchedTokensCount = tokens.filter((token) => tokenMatchesText(token, searchableText)).length

	// Require every token to match for short queries (1-2 words). For longer queries,
	// require a true majority (strictly more than half) rather than just half, so stories
	// with fuzzy/word-root matches on only a couple of words are less likely to be
	// treated as relevant noise while still allowing one odd word to be forgiven.
	const minimumMatches = tokens.length <= 2 ? tokens.length : Math.ceil((tokens.length + 1) / 2)

	return matchedTokensCount >= minimumMatches
}

const parseSavedSlugs = (filterList) => {
	if (!filterList || filterList === "false_attribute:false") {
		return []
	}

	const matches = [...filterList.matchAll(/slug:([^\s)]+)/g)]
	return matches.map((match) => match[1])
}

const toDateValue = (value) => {
	if (!value) {
		return 0
	}

	const timestamp = Date.parse(value)
	return Number.isNaN(timestamp) ? 0 : timestamp
}

const getLocalizedLabel = (value, translationMap, locale) => {
	return translationMap[value]?.[locale] || value
}

const getFilterCounts = (items, field, translationMap, locale) => {
	const counts = new Map()

	items.forEach((item) => {
		const values = item[field] || []
		values.forEach((value) => {
			counts.set(value, (counts.get(value) || 0) + 1)
		})
	})

	return orderBy(
		Array.from(counts.entries()).map(([value, count]) => ({
			value,
			label: getLocalizedLabel(value, translationMap, locale),
			count,
		})),
		["label", "count"],
		["asc", "desc"]
	)
}

const storyMatchesRefinements = (story, refinementList) => {
	const personalityValues = story.story_personality_filter || []
	const interestValues = story.story_interests_filter || []
	const identityValues = story.story_identity_filter || []
	const educationValues = story.story_required_education_filter || []

	const matchesPersonality =
		refinementList.story_personality_filter.length === 0
		|| refinementList.story_personality_filter.some((value) => personalityValues.includes(value))

	const matchesInterests =
		refinementList.story_interests_filter.length === 0
		|| refinementList.story_interests_filter.some((value) => interestValues.includes(value))

	const matchesIdentity =
		refinementList.story_identity_filter.length === 0
		|| refinementList.story_identity_filter.every((value) => identityValues.includes(value))

	const matchesEducation =
		refinementList.story_required_education_filter.length === 0
		|| refinementList.story_required_education_filter.some((value) => educationValues.includes(value))

	return matchesPersonality && matchesInterests && matchesIdentity && matchesEducation
}

const getStoryRecencyDate = (story) => {
	return story?.created_at
		|| story?.simplified_version_created_at
		|| story?.modified_at
		|| story?.published_at
}

const getDefaultSearchState = () => ({
	query: "",
	refinementList: initialRefinementList,
	page: 1,
})

const normalizeSearchState = (state) => {
	const defaultState = getDefaultSearchState()
	const normalizedPage = Math.max(1, Number(state?.page) || 1)

	return {
		...defaultState,
		...(state || {}),
		refinementList: {
			...initialRefinementList,
			...(state?.refinementList || {}),
		},
		page: normalizedPage,
	}
}

const StoriesExplore = ({ classContext = "stories", filterList }) => {
	const { locale, formatMessage } = useIntl()

	const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
	const [sortByPageQuery, setSortByPageQuery] = useQueryParam("sortByPageQuery", StringParam)

	const [sortByPage, setSortByPage] = useState(sortByPageQuery || "NEWEST")
	const [searchState, setSearchState] = useState(normalizeSearchState(queryFilters))
	const deferredQuery = useDeferredValue(searchState.query)

	const debouncedSetState = useRef(null)

	const allStories = useAllStories(locale)

	const savedSlugs = useMemo(() => parseSavedSlugs(filterList), [filterList])

	useEffect(() => {
		setSortByPage(sortByPageQuery || "NEWEST")
	}, [sortByPageQuery])

	useEffect(() => {
		setSearchState(normalizeSearchState(queryFilters))
	}, [queryFilters])

	useEffect(() => {
		return () => {
			clearTimeout(debouncedSetState.current)
		}
	}, [])

	const storiesBySort = useMemo(() => {
		const sortedStories = [...allStories].sort((a, b) => {
			return toDateValue(getStoryRecencyDate(b)) - toDateValue(getStoryRecencyDate(a))
		})

		if (sortByPage === "SAVED") {
			const savedSet = new Set(savedSlugs)
			return sortedStories.filter((story) => savedSet.has(story.slug))
		}

		return sortedStories
	}, [allStories, sortByPage, savedSlugs])

	const storiesByQuery = useWeightedFuseSearch({
		items: storiesBySort,
		query: deferredQuery,
		keys: storiesSearchKeys,
		minQueryLength: 2,
		fuseOptions: storiesSearchOptions,
		// Cuts off weaker Fuse matches once the query is long enough to be meaningful,
		// so the long tail of loosely-related stories is trimmed and fewer/lower-relevance
		// pages of results are shown, instead of keeping every match under the raw threshold.
		maxMatchScore: 0.1,
		minQueryLengthForScoreFilter: 4,
		fallbackToUnfilteredOnEmpty: true,
	})

	const refinementList = searchState.refinementList || initialRefinementList

	const ensureSelectedOptionsVisible = (options, selectedValues, translationMap) => {
		const optionMap = new Map(options.map((option) => [option.value, option]))

		selectedValues.forEach((value) => {
			if (!optionMap.has(value)) {
				optionMap.set(value, {
					value,
					label: getLocalizedLabel(value, translationMap, locale),
					count: 0,
				})
			}
		})

		return orderBy(Array.from(optionMap.values()), ["label", "count"], ["asc", "desc"])
	}

	const filteredStories = useMemo(() => {
		const strictTokenMatches = storiesByQuery
			.filter((story) => storyMatchesRefinements(story, refinementList))
			.filter((story) => storyMatchesSearchTokens(story, deferredQuery))

		const normalizedQuery = (deferredQuery || "").trim()
		const matchingStories = strictTokenMatches.length === 0 && normalizedQuery.length >= 2
			? storiesByQuery.filter((story) => storyMatchesRefinements(story, refinementList))
			: strictTokenMatches

		// Keep weighted Fuse relevance ordering when actively searching, but hard-boost referenced-career synonym matches.
		if (normalizedQuery.length >= 2) {
			const rankedMatches = matchingStories
				.map((story, index) => ({
					story,
					index,
					priority: getStoryCareerSynonymPriority(story, normalizedQuery),
				}))
				.sort((a, b) => (b.priority - a.priority) || (a.index - b.index))
				.map((entry) => entry.story)

			const existingStoryIds = new Set(rankedMatches.map((story) => getStoryIdentity(story)))
			// Pull additional synonym matches from the Fuse/score-filtered candidate pool
			// (storiesByQuery), not the full unfiltered dataset (storiesBySort) - otherwise
			// the maxMatchScore cutoff above has no effect on the final result count, since
			// any story with a synonym substring match would be added back in regardless of score.
			const additionalSynonymMatches = storiesByQuery
				.filter((story) => !existingStoryIds.has(getStoryIdentity(story)))
				.filter((story) => storyMatchesRefinements(story, refinementList))
				.filter((story) => getStoryCareerSynonymPriority(story, normalizedQuery) > 0)

			if (additionalSynonymMatches.length === 0) {
				return rankedMatches
			}

			return [...rankedMatches, ...additionalSynonymMatches]
				.map((story, index) => ({
					story,
					index,
					priority: getStoryCareerSynonymPriority(story, normalizedQuery),
				}))
				.sort((a, b) => (b.priority - a.priority) || (a.index - b.index))
				.map((entry) => entry.story)
		}

		return matchingStories.sort((a, b) => {
			return toDateValue(getStoryRecencyDate(b)) - toDateValue(getStoryRecencyDate(a))
		})
	}, [storiesByQuery, storiesBySort, refinementList, deferredQuery])

	const totalPages = Math.max(1, Math.ceil(filteredStories.length / STORIES_PER_PAGE))
	const currentPage = Math.min(searchState.page || 1, totalPages)

	const paginatedStories = useMemo(() => {
		const startIndex = (currentPage - 1) * STORIES_PER_PAGE
		return filteredStories.slice(startIndex, startIndex + STORIES_PER_PAGE)
	}, [filteredStories, currentPage])

	const personalityOptions = useMemo(() => {
		const options = getFilterCounts(allStories, "story_personality_filter", localizePersonalityCheckboxTranslation, locale)

		return ensureSelectedOptionsVisible(options, refinementList.story_personality_filter, localizePersonalityCheckboxTranslation)
	}, [allStories, refinementList, locale])

	const interestOptions = useMemo(() => {
		const options = getFilterCounts(allStories, "story_interests_filter", localizeInterestCheckboxTranslation, locale)

		return ensureSelectedOptionsVisible(options, refinementList.story_interests_filter, localizeInterestCheckboxTranslation)
	}, [allStories, refinementList, locale])

	const identityOptions = useMemo(() => {
		const options = getFilterCounts(allStories, "story_identity_filter", localizeIdentityCheckboxTranslation, locale)

		return ensureSelectedOptionsVisible(options, refinementList.story_identity_filter, localizeIdentityCheckboxTranslation)
	}, [allStories, refinementList, locale])

	const educationOptions = useMemo(() => {
		const options = getFilterCounts(allStories, "story_required_education_filter", localizeEducationCheckboxTranslations, locale)

		return ensureSelectedOptionsVisible(options, refinementList.story_required_education_filter, localizeEducationCheckboxTranslations)
	}, [allStories, refinementList, locale])

	const updateSearchState = (nextState, debounceQuery = false) => {
		setSearchState(nextState)

		clearTimeout(debouncedSetState.current)

		if (debounceQuery) {
			debouncedSetState.current = setTimeout(() => {
				setQueryFilters(nextState)
			}, 1000)
			return
		}

		setQueryFilters(nextState)
	}

	const handleQueryChange = (event) => {
		const nextState = {
			...searchState,
			query: event.target.value,
			page: 1,
		}

		updateSearchState(nextState, true)
	}

	const handleClearQuery = () => {
		const nextState = {
			...searchState,
			query: "",
			page: 1,
		}
		updateSearchState(nextState)
	}

	const handleSortByClick = (event, page) => {
		event.preventDefault()
		if (page === sortByPage) {
			return
		}

		setSortByPage(page)
		setSortByPageQuery(page)
		updateSearchState({
			query: "",
			refinementList: initialRefinementList,
			page: 1,
		})
	}

	const toggleRefinementValue = (field, value) => {
		const currentValues = refinementList[field] || []
		const nextValues = currentValues.includes(value)
			? currentValues.filter((item) => item !== value)
			: [...currentValues, value]

		updateSearchState({
			...searchState,
			refinementList: {
				...refinementList,
				[field]: nextValues,
			},
			page: 1,
		})
	}

	const clearFilters = () => {
		updateSearchState({
			...searchState,
			refinementList: initialRefinementList,
			page: 1,
		})
	}

	const selectedFilterOptionIds = useMemo(() => {
		return Object.entries(refinementList).flatMap(([field, values]) => {
			return (values || []).map((value) => `${field}${FILTER_ID_SEPARATOR}${value}`)
		})
	}, [refinementList])

	const filterSections = useMemo(() => {
		return [
			{
				id: "story_personality_filter",
				title: formatMessage({ id: "algolia-stories-explore-personality", defaultMessage: "Personality" }),
				columns: 3,
				options: personalityOptions.map((option) => ({
					id: `story_personality_filter${FILTER_ID_SEPARATOR}${option.value}`,
					label: option.label,
					count: option.count,
				})),
			},
			{
				id: "story_interests_filter",
				title: formatMessage({ id: "algolia-stories-explore-interests", defaultMessage: "Interests" }),
				columns: 3,
				options: interestOptions.map((option) => ({
					id: `story_interests_filter${FILTER_ID_SEPARATOR}${option.value}`,
					label: option.label,
					count: option.count,
				})),
			},
			{
				id: "story_identity_filter",
				title: formatMessage({ id: "algolia-stories-explore-identity", defaultMessage: "Identity" }),
				columns: 3,
				options: identityOptions.map((option) => ({
					id: `story_identity_filter${FILTER_ID_SEPARATOR}${option.value}`,
					label: option.label,
					count: option.count,
				})),
			},
			{
				id: "story_required_education_filter",
				title: formatMessage({ id: "algolia-stories-explore-education", defaultMessage: "Education" }),
				columns: 3,
				options: educationOptions.map((option) => ({
					id: `story_required_education_filter${FILTER_ID_SEPARATOR}${option.value}`,
					label: option.label,
					count: option.count,
				})),
			},
		]
	}, [
		educationOptions,
		formatMessage,
		identityOptions,
		interestOptions,
		personalityOptions,
	])

	const handleUnifiedFilterToggle = (optionId) => {
		const [field, value] = optionId.split(FILTER_ID_SEPARATOR)

		if (!field || !value) {
			return
		}

		toggleRefinementValue(field, value)
	}

	const handlePageChange = (nextPage) => {
		if (nextPage < 1 || nextPage > totalPages) {
			return
		}

		updateSearchState({
			...searchState,
			page: nextPage,
		})

		window.scrollTo({ top: 0, behavior: "smooth" })
	}

	return (
		<>
			<div className="ais-SearchBox">
				<form className="ais-SearchBox-form" onSubmit={(event) => event.preventDefault()}>
					<input
						className="ais-SearchBox-input"
						type="search"
						value={searchState.query || ""}
						onChange={handleQueryChange}
						placeholder={formatMessage({
							id: "algolia-stories-explore-search-placeholder",
							defaultMessage: "Search by keyword, job title, name, company, location",
						})}
					/>
					{(searchState.query || "").length > 0 && (
						<button type="button" className="ais-SearchBox-reset" onClick={handleClearQuery}>
							<FontAwesomeIcon icon={faTimes} />
						</button>
					)}
					<button type="submit" className="ais-SearchBox-submit">
						<FontAwesomeIcon icon={faSearch} />
					</button>
				</form>
			</div>

			<div className="ais-ScrollTo">
				<div className={`w-default-algolia-card-hits-container stream stream-${classContext} stream-${classContext}-explore`}>
					<div className="stream-explore-filters">
						<ul className="explore-filters-list">
							<li className="explore-filters-list-item explore-filters-list-item-sort">
								<FormattedMessage id="algolia-stories-view-sort-by" defaultMessage="Sort by:" />
							</li>

							<li className="explore-filters-list-item explore-filters-list-item-newest">
								<a href="/" className={sortByPage !== "SAVED" ? "active" : ""} onClick={(event) => handleSortByClick(event, "NEWEST")}>
									<FormattedMessage id="algolia-stories-view-newest" defaultMessage="Newest" />
								</a>
							</li>
							<li className="explore-filters-list-item explore-filters-list-item-saved">
								<a href="/" className={sortByPage === "SAVED" ? "active" : ""} onClick={(event) => handleSortByClick(event, "SAVED")}>
									<FormattedMessage id="algolia-stories-view-saved" defaultMessage="Saved" />
								</a>
							</li>
						</ul>

						<ExploreUnifiedFiltersDropdown
							heading={formatMessage({ id: "filter-people-by", defaultMessage: "Filter People" })}
							viewLabel={formatMessage({ id: "algolia-toggle-dropdown-people", defaultMessage: "People" })}
							sections={filterSections}
							selectedOptionIds={selectedFilterOptionIds}
							onToggleOption={handleUnifiedFilterToggle}
							onClearAll={clearFilters}
						/>
					</div>

				<div className={`ais-Hits ${paginatedStories.length === 0 ? "ais-Hits-empty" : ""}`.trim()}>
					<ul className="ais-Hits-list">
						{paginatedStories.map((story) => (
							<li key={story.id} className="ais-Hits-item">
								<WingsStoryCard
									objectID={story.objectID || story.id}
									img={story.story_personal_photo}
									video={story.story_video_url}
									title={story.title}
									slug={story.slug}
									storyJobTitle={story.story_job_title}
									storyCompany={story.story_company}
									storyQuote={story.story_quote}
									contentType={story.type}
								/>
							</li>
						))}
					</ul>

					{paginatedStories.length === 0 && (
						<p className="ais-Hits-empty-message">
							<FormattedMessage id="algolia-empty-state" defaultMessage="No results found." />
						</p>
					)}
				</div>

					<WingsPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
				</div>
			</div>
		</>
	)
}

export default StoriesExplore
